import { defineStore } from 'pinia'
import type { InversedMapBounds, MapKeyGroup } from '#cmui/types/map'
import type { MapMarkersRequestPayload, MappyResponse } from '~~/interfaces/map'
import { $fetchApi } from '~~/app/composables/fetchApi'

const useMapStore = defineStore('map', () => {
    const baseUrl = tryUseNuxtApp()
        ? useRuntimeConfig().public.APPLICATION_URL
        : process.env.NUXT_PUBLIC_APPLICATION_URL

    const loadingMap = ref(false)

    async function fetchKeyData() {
        loadingMap.value = true

        return $fetchApi<MapKeyGroup[]>(`${baseUrl}/mappy/legend.json`, {
            query: { locale: useLanguage() },
            skipTokenRefresh: true
        })
            .then((response) => response)
            .finally(() => {
                loadingMap.value = false
            })
    }

    async function fetchMapMarkers(
        bounds: InversedMapBounds,
        networkId: number | undefined = undefined,
        city: string | undefined = undefined,
        countryId: number | undefined = undefined,
        canChargeWithChargemap: boolean | undefined = undefined
    ) {
        if (
            !bounds?.NW?.lat ||
            !bounds?.NW?.lng ||
            !bounds?.SE?.lat ||
            !bounds?.SE?.lng
        ) {
            return {
                items: []
            }
        }

        const payload: MapMarkersRequestPayload = {
            NW: [`${bounds.NW.lat};${bounds.NW.lng}`],
            SE: [`${bounds.SE.lat};${bounds.SE.lng}`]
        }

        loadingMap.value = true

        const params: any = {
            network: networkId,
            state: [2]
        }

        if (city) {
            params.city = city
        }

        if (countryId) {
            params.country = countryId
        }

        if (city && countryId) {
            params.offset = 0
        }

        if (canChargeWithChargemap !== undefined) {
            params.can_charge_with_chargemap = canChargeWithChargemap
        }

        return $fetchApi<MappyResponse>(
            `${baseUrl}/mappy/charging_pools.json`,
            {
                query: {
                    ...payload,
                    ...params
                },
                skipTokenRefresh: true
            }
        )
            .then((responseFromMappy) => ({
                items: responseFromMappy.response?.content?.items
            }))
            .finally(() => {
                loadingMap.value = false
            })
    }

    return {
        // Data
        loadingMap,

        // Methods
        fetchKeyData,
        fetchMapMarkers
    }
})

type MapStore = Omit<
    ReturnType<typeof useMapStore>,
    keyof ReturnType<typeof defineStore>
>

export default useMapStore

export type { MapStore }
